import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { sitesApi, CAT_LABEL } from '../services/tourismeApi';
import './Tourisme.css';

const CAT_ACCENT = {
  religieux:   '#E76F51',
  culturel:    '#1B6985',
  nature:      '#34A853',
  patrimoine:  '#C9A961',
  gastronomie: '#A93D29',
};

function imgFor(s) {
  if (s.mainImageUrl) return s.mainImageUrl;
  const seed = s.mainImageSeed || `cb-tour-${s.id}`;
  return `https://picsum.photos/seed/${seed}/2000/1200`;
}

const SiteDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    window.scrollTo({ top: 0, behavior: 'instant' });    // scroll au top à l'arrivée
    sitesApi.get(slug)
      .then((data) => { if (!cancelled) { setSite(data.site); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [slug]);

  // Retour : aller à /tourisme et scroller en haut (ou sur l'ancre #sites)
  const goBack = (e) => {
    e.preventDefault();
    navigate('/tourisme');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 0);
  };

  if (loading) {
    return (
      <main className="tour-page">
        <div className="wrap" style={{ padding: 80 }}>
          <p className="tour-muted">Chargement…</p>
        </div>
      </main>
    );
  }
  if (error || !site) {
    return (
      <main className="tour-page">
        <div className="wrap" style={{ padding: 80 }}>
          <p className="tour-muted" style={{ marginBottom: 16 }}>Site introuvable.</p>
          <Link to="/tourisme" onClick={goBack} className="tour-pill">← Retour à la liste</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="tour-page">
      {/* ============ HERO sobre, fond noir, sans border-radius ============ */}
      <header className="sd-hero">
        <div className="sd-hero-inner">
          <div className="sd-hero-top">
            <Link to="/tourisme" onClick={goBack} className="sd-back">← Retour aux sites</Link>
            <span className="sd-cat" style={{ background: CAT_ACCENT[site.category] }}>
              {CAT_LABEL[site.category]}
            </span>
          </div>
          <h1>{site.title}</h1>
          {site.shortDescription && <p className="sd-lede">{site.shortDescription}</p>}
          <div className="sd-meta-row">
            {site.address && <span>📍 {site.address.split(',')[0]}</span>}
            {site.openingHours && (<><span className="sep" />· <span>{site.openingHours}</span></>)}
            {site.entryFee && (<><span className="sep" />· <span>{site.entryFee}</span></>)}
          </div>
        </div>
      </header>

      {/* ============ IMAGE pleine largeur (séparée du hero, sans radius) ============ */}
      {(site.mainImageUrl || site.mainImageSeed) && (
        <figure className="sd-figure">
          <div className="sd-figure-img" style={{ backgroundImage: `url('${imgFor(site)}')` }} />
        </figure>
      )}

      {/* ============ CORPS ============ */}
      <section className="tour-section sd-section">
        <div className="sd-content">
          {site.description && (
            <p className="sd-desc">{site.description}</p>
          )}

          <div className="sd-info">
            {site.address      && <InfoCell label="Adresse"        value={site.address} />}
            {site.openingHours && <InfoCell label="Horaires"       value={site.openingHours} />}
            {site.entryFee     && <InfoCell label="Tarif"          value={site.entryFee} />}
            {site.phone        && <InfoCell label="Téléphone"      value={site.phone} />}
            {site.email        && <InfoCell label="Email"          value={site.email} />}
            {site.lat != null && site.lng != null && (
              <InfoCell
                label="Coordonnées GPS"
                value={(
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${site.lat}&mlon=${site.lng}#map=17/${site.lat}/${site.lng}`}
                    target="_blank" rel="noopener noreferrer"
                    className="sd-link"
                  >
                    {site.lat.toFixed(4)}, {site.lng.toFixed(4)} ↗
                  </a>
                )}
              />
            )}
          </div>

          {site.tips && (
            <div className="sd-tips">
              <small>Conseil visiteur</small>
              <p>{site.tips}</p>
            </div>
          )}

          <Link to="/tourisme" onClick={goBack} className="sd-back-link">← Tous les sites</Link>
        </div>
      </section>
    </main>
  );
};

const InfoCell = ({ label, value }) => (
  <div className="sd-info-cell">
    <small>{label}</small>
    <div>{value}</div>
  </div>
);

export default SiteDetail;
